<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Account } from '../../firebase/services/accounts.service'
import { Customer } from '../../firebase/services/customers.service'
import { Supplier } from '../../firebase/services/suppliers.service'
import { useBankStore } from '../../stores/bank'
import { formatCurrency, formatDate } from '../../utils/format'

// Carregar bancos
const bankStore = useBankStore()
bankStore.fetchBanks()

// Controle da aba ativa
const activeTab = ref('geral')

// Lista de abas
const tabs = computed(() => {
  const bankTabs = bankStore.banks.map(bank => ({
    id: bank.id,
    name: bank.name,
    type: 'bank'
  }))

  return [
    { id: 'geral', name: 'Geral', type: 'fixed' },
    ...bankTabs
  ]
})

// Criar transações de saldo inicial para cada banco
const initialBalanceTransactions = computed(() => {
  return bankStore.banks.map(bank => ({
    id: `initial-${bank.id}`,
    date: bank.createdAt || new Date(0).toISOString(), // Data mais antiga possível se não houver createdAt
    type: 'initial' as const,
    value: bank.initialBalance || 0,
    bankId: bank.id
  }))
})

const getBankName = (account: Account): string => {
  if (!account.bankId) return '-'
  const bank = bankStore.banks.find(b => b.id === account.bankId)
  return bank ? bank.name : '-'
}

// Função para obter a data efetiva da transação
function getEffectiveDate(account: Account): Date {
  // Se tem data agendada, usa ela
  if (account.scheduledDate) {
    return new Date(account.scheduledDate)
  }
  // Se não tem data agendada, usa a data de vencimento
  return new Date(account.dueDate)
}

// Combinar transações normais com saldos iniciais
const allTransactions = computed(() => {
  // Começar com os saldos iniciais
  let transactions = []
  
  // Na aba geral, mostrar saldo inicial de todos os bancos
  if (activeTab.value === 'geral') {
    transactions = [...initialBalanceTransactions.value]
  } 
  // Em uma aba específica, mostrar apenas o saldo inicial daquele banco
  else {
    const bankInitialBalance = initialBalanceTransactions.value.find(t => t.bankId === activeTab.value)
    if (bankInitialBalance) {
      transactions = [bankInitialBalance]
    }
  }

  // Adicionar as contas filtradas
  transactions = [...transactions, ...filteredAccounts.value]

  return transactions.sort((a, b) => {
    // Saldo inicial sempre primeiro
    if (a.type === 'initial') return -1
    if (b.type === 'initial') return 1
    // Demais transações por data efetiva
    return getEffectiveDate(a).getTime() - getEffectiveDate(b).getTime()
  })
})

const props = defineProps<{
  accounts: Account[]
  customers: Customer[]
  suppliers: Supplier[]
  transactions?: any[]
  type: 'current' | 'future'
  loading?: boolean
}>()

const filteredAccounts = computed(() => {
  const startDateValue = new Date(startDate.value)
  const endDateValue = new Date(endDate.value)

  let accounts = props.accounts
    .filter(account => {
      // Determinar o status real da conta
      const status = determineAccountStatus(account)
      
      // Mostrar apenas contas PAGAS
      if (status !== 'PAGO') {
        return false
      }

      // Usar a data efetiva para filtrar
      const effectiveDate = getEffectiveDate(account)
      return effectiveDate >= startDateValue && effectiveDate <= endDateValue
    })

  // Filtrar por banco se não estiver na aba geral
  if (activeTab.value !== 'geral') {
    accounts = accounts.filter(account => account.bankId === activeTab.value)
  }

  return accounts
})

// Função para determinar o status real da conta
function determineAccountStatus(account) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const dueDate = new Date(account.dueDate)
  dueDate.setHours(0, 0, 0, 0)
  
  const scheduledDate = account.scheduledDate ? new Date(account.scheduledDate) : null
  if (scheduledDate) scheduledDate.setHours(0, 0, 0, 0)
  
  // Se a conta está cancelada
  if (account.status === 'CANCELADO') return 'CANCELADO'
  
  // Se tem data agendada
  if (scheduledDate) {
    // Se a data agendada já passou, conta está paga
    if (scheduledDate < today) {
      return 'PAGO'
    }
    // Se a data agendada é hoje ou futura, conta está agendada
    return 'AGENDADO'
  }
  
  // Se não tem data agendada, verifica o vencimento
  if (dueDate < today) {
    return 'ATRASADO'
  }
  
  // Se a data de vencimento é hoje ou futura, está pendente
  return 'PENDENTE'
}

// Calcular totais incluindo saldo inicial quando aplicável
const totals = computed(() => {
  let initialBalanceTotal = 0

  // Na aba geral, somar todos os saldos iniciais
  if (activeTab.value === 'geral') {
    initialBalanceTotal = initialBalanceTransactions.value.reduce((sum, t) => sum + t.value, 0)
  } 
  // Em uma aba específica, usar apenas o saldo inicial do banco selecionado
  else {
    const bankInitialBalance = initialBalanceTransactions.value.find(t => t.bankId === activeTab.value)
    if (bankInitialBalance) {
      initialBalanceTotal = bankInitialBalance.value
    }
  }

  const receivable = filteredAccounts.value
    .filter(a => a.type === 'receivable')
    .reduce((sum, a) => sum + a.value, 0)

  const payable = filteredAccounts.value
    .filter(a => a.type === 'payable')
    .reduce((sum, a) => sum + a.value, 0)

  return {
    receivable: receivable + (initialBalanceTotal > 0 ? initialBalanceTotal : 0),
    payable: payable + (initialBalanceTotal < 0 ? Math.abs(initialBalanceTotal) : 0),
    balance: initialBalanceTotal + receivable - payable
  }
})

const getEntityName = (account: Account): string => {
  if (account.type === 'payable') {
    const supplier = props.suppliers.find(s => s.id === account.supplierId)
    return supplier ? supplier.name : 'Fornecedor não encontrado'
  } else {
    const customer = props.customers.find(c => c.id === account.customerId)
    return customer ? customer.name : 'Cliente não encontrado'
  }
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('pt-BR')
}

// Função para calcular o saldo acumulado
const calculateRunningBalance = (transactions) => {
  let runningBalance = 0
  let lastDate = null
  
  return transactions.map(transaction => {
    // Para saldo inicial
    if (transaction.type === 'initial') {
      runningBalance = transaction.value
      return { ...transaction, runningBalance }
    }

    // Para transações normais
    const effectiveDate = getEffectiveDate(transaction)
    const isNewDay = !lastDate || effectiveDate.toDateString() !== lastDate.toDateString()
    
    // Se é um novo dia, adicionar uma linha de separação
    if (isNewDay && lastDate !== null) {
      runningBalance = runningBalance // Mantém o saldo do dia anterior
    }
    
    // Atualiza o saldo
    if (transaction.type === 'receivable') {
      runningBalance += transaction.value
    } else if (transaction.type === 'payable') {
      runningBalance -= transaction.value
    }
    
    lastDate = effectiveDate
    return { ...transaction, runningBalance, isNewDay }
  })
}

// Transações com saldo calculado
const transactionsWithBalance = computed(() => {
  return calculateRunningBalance(allTransactions.value)
})

// Data inicial: primeiro dia do mês atual
const startDate = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0])
// Data final: dia atual
const endDate = ref(new Date().toISOString().split('T')[0])

// Função para definir o mês atual
const setCurrentMonth = () => {
  const now = new Date()
  startDate.value = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
  endDate.value = new Date().toISOString().split('T')[0]
}

// Observador para mudanças nas datas
watch([startDate, endDate], ([newStartDate, newEndDate]) => {
  // Aqui você pode adicionar a lógica para filtrar as transações pelo período
  console.log('Período selecionado:', { inicio: newStartDate, fim: newEndDate })
})
</script>

<template>
  <div>
    <!-- Tabs e Datepickers -->
    <div class="border-b border-gray-200 mb-6 flex justify-between items-center">
      <nav class="-mb-px flex space-x-8" aria-label="Tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="[
            activeTab === tab.id
              ? 'border-primary-dark text-primary-dark'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
          ]"
        >
          {{ tab.name }}
        </button>
      </nav>

      <!-- Datepickers -->
      <div class="flex items-center space-x-4">
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-500">De:</span>
          <input 
            type="date" 
            v-model="startDate"
            :max="endDate"
            class="block rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark sm:text-sm"
          >
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-gray-500">Até:</span>
          <input 
            type="date" 
            v-model="endDate"
            :min="startDate"
            class="block rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark sm:text-sm"
          >
        </div>
        <button
          @click="setCurrentMonth"
          class="inline-flex items-center px-3 py-2 border border-primary-dark text-sm font-medium rounded-md text-primary-dark bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark"
        >
          <i class="pi pi-calendar-plus mr-2"></i>
          Mês Atual
        </button>
      </div>
    </div>

    <!-- Accounts Table -->
    <div class="bg-white rounded-lg border border-gray-100">
      <div class="sticky top-0 bg-gray-50 border-b border-gray-100 z-10">
        <table class="min-w-full">
          <thead>
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Data</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente/Fornecedor</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">Banco</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">Tipo</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">Crédito</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">Débito</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">Saldo</th>
            </tr>
          </thead>
        </table>
      </div>

      <div class="overflow-y-auto" style="max-height: 500px;">
        <table class="min-w-full">
          <tbody class="bg-white divide-y divide-gray-100">
            <template v-for="(transaction, index) in transactionsWithBalance" :key="transaction.id">
              <!-- Linha de separação de dia -->
              <tr v-if="transaction.isNewDay" class="bg-gray-50">
                <td colspan="7" class="px-6 py-2 text-sm font-semibold text-gray-500">
                  Saldo do dia anterior: {{ formatCurrency(transactionsWithBalance[index-1]?.runningBalance || 0) }}
                </td>
              </tr>
              
              <!-- Linha da transação -->
              <tr :class="{'bg-gray-50': transaction.type === 'initial'}">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-32">
                  {{ transaction.type === 'initial' ? 'Saldo Inicial' : formatDate(getEffectiveDate(transaction)) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ transaction.type === 'initial' ? '-' : getEntityName(transaction) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-64">
                  {{ transaction.type === 'initial' ? getBankName({ bankId: transaction.bankId }) : getBankName(transaction) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-48">
                  <span :class="[
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    transaction.type === 'initial' ? 'bg-blue-100 text-blue-800' :
                    transaction.type === 'receivable' ? 'bg-emerald-100 text-emerald-800' : 
                    'bg-rose-100 text-rose-800'
                  ]">
                    {{ transaction.type === 'initial' ? 'Saldo Inicial' : 
                       transaction.type === 'receivable' ? 'Receita' : 'Despesa' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-left text-emerald-600 w-36">
                  {{ (transaction.type === 'initial' && transaction.value > 0) || transaction.type === 'receivable' 
                     ? formatCurrency(transaction.value) : '' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-left text-rose-600 w-36">
                  {{ (transaction.type === 'initial' && transaction.value < 0) || transaction.type === 'payable'
                     ? formatCurrency(Math.abs(transaction.value)) : '' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-left font-semibold w-36" 
                    :class="{'text-emerald-600': transaction.runningBalance > 0, 
                            'text-rose-600': transaction.runningBalance < 0}">
                  {{ formatCurrency(transaction.runningBalance) }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="sticky bottom-0 bg-gray-50 border-t border-gray-100 z-10">
        <table class="min-w-full">
          <tfoot>
            <tr class="font-medium text-base">
              <td colspan="4" class="px-6 py-5 text-gray-900 font-bold">
                Total
              </td>
              <td class="px-6 py-5 text-left text-emerald-600 font-semibold w-36">
                {{ formatCurrency(totals.receivable) }}
              </td>
              <td class="px-6 py-5 text-left text-rose-600 font-semibold w-36">
                {{ formatCurrency(totals.payable) }}
              </td>
              <td class="px-6 py-5 text-left font-bold text-lg w-36" 
                  :class="{'text-emerald-600': totals.balance > 0, 
                          'text-rose-600': totals.balance < 0}">
                {{ formatCurrency(totals.balance) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>